import { Loader2, Plus } from 'lucide-react';
import type { MemberTypeConfig } from '@shared/api.interface';
import { Button } from '@/components/ui/button';

type Mode = 'monthly' | 'total';
const cardClass = 'rounded-2xl bg-white shadow-[0_0_0_1px_var(--border)]';
const gridClass =
  'grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_88px_150px] items-center gap-3 px-5';
const enableClass =
  'border-emerald-200 text-emerald-800 hover:bg-emerald-50 hover:text-emerald-900';
// 删除不常用且不可恢复，入口放在编辑弹窗底部，列表只保留“编辑”
const memberCountText = (c: MemberTypeConfig) =>
  c.member_count > 0 ? `${c.member_count} 位` : '无人使用';

function StatusPill({ status }: { status: MemberTypeConfig['status'] }) {
  const on = status === '启用';
  return (
    <span
      className={
        'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ' +
        (on
          ? 'bg-emerald-50 text-emerald-800'
          : 'bg-muted text-muted-foreground')
      }
    >
      {on ? '启用' : '已停用'}
    </span>
  );
}

export function MemberTypePanel({
  configs,
  loading,
  mode,
  onModeChange,
  onAdd,
  onEdit,
  onToggleStatus,
}: {
  configs: MemberTypeConfig[];
  loading: boolean;
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  onAdd: () => void;
  onEdit: (config: MemberTypeConfig) => void;
  /** 停用（需确认）或重新启用 */
  onToggleStatus: (config: MemberTypeConfig) => void;
}) {
  // 已停用的类型排在最后
  const rows = configs
    .filter((c) => (mode === 'monthly' ? c.quota_mode !== 'total' : c.quota_mode === 'total'))
    .sort((a, b) => Number(b.status === '启用') - Number(a.status === '启用'));
  const isOff = (c: MemberTypeConfig) => c.status !== '启用';
  const quotaHead = mode === 'monthly' ? '每月额度' : '年度总次数';
  const quota = (c: MemberTypeConfig) =>
    mode === 'monthly' ? `${c.monthly_quota} 次/月` : `${c.total_quota} 次/年`;
  const modeLabel = mode === 'monthly' ? '按月配额' : '年度总次数';

  return (
    <div className="space-y-3.5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          role="group"
          aria-label="配额模式"
          className="inline-flex gap-0.5 self-start rounded-xl bg-muted p-1"
        >
          {(
            [
              ['monthly', '按月配额'],
              ['total', '年度总次数'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              onClick={() => onModeChange(value)}
              className={
                'min-h-9 whitespace-nowrap rounded-[9px] px-3.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-cta ' +
                (mode === value
                  ? 'bg-card font-semibold text-cta shadow-sm'
                  : 'text-accent-foreground hover:text-foreground')
              }
            >
              {label}
            </button>
          ))}
        </div>
        <Button
          onClick={onAdd}
          className="h-11 rounded-xl bg-cta px-[18px] font-semibold text-cta-foreground hover:bg-cta-hover"
        >
          <Plus className="mr-1 h-4 w-4" />
          新增{modeLabel}类型
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* 电脑：表格 */}
          <div className={cardClass + ' hidden overflow-hidden lg:block'}>
            <div
              className={
                gridClass +
                ' h-[42px] bg-background text-[13px] font-medium text-muted-foreground'
              }
            >
              <span>类型名称</span>
              <span>{quotaHead}</span>
              <span>单日上限</span>
              <span>有效期</span>
              <span>在用会员</span>
              <span>状态</span>
              <span className="text-right">操作</span>
            </div>
            {rows.map((c) => (
              <div
                key={c.id}
                className={
                  gridClass +
                  ' min-h-14 border-t border-border/60 text-sm' +
                  (isOff(c) ? ' bg-background/60 text-muted-foreground' : '')
                }
              >
                <span className="truncate font-semibold" title={c.name}>
                  {c.name}
                </span>
                <span className="tabular-nums">{quota(c)}</span>
                <span className="tabular-nums">{c.daily_limit} 次/日</span>
                <span className="tabular-nums">{c.validity_months} 个月</span>
                <span className="tabular-nums text-muted-foreground">
                  {memberCountText(c)}
                </span>
                <span>
                  <StatusPill status={c.status} />
                </span>
                <span className="flex justify-end gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 rounded-[10px] text-foreground"
                    onClick={() => onEdit(c)}
                  >
                    编辑
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={'h-9 rounded-[10px] ' + (isOff(c) ? enableClass : '')}
                    onClick={() => onToggleStatus(c)}
                  >
                    {isOff(c) ? '启用' : '停用'}
                  </Button>
                </span>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="border-t border-border/60 py-10 text-center text-sm text-muted-foreground">
                暂无{modeLabel}类型
              </p>
            )}
          </div>

          {/* 手机：卡片 */}
          <div className="space-y-2.5 lg:hidden">
            {rows.map((c) => (
              <article
                key={c.id}
                className={
                  cardClass + ' space-y-3 p-3.5' + (isOff(c) ? ' bg-background text-muted-foreground' : '')
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 break-words text-base font-bold">
                    {c.name}
                  </span>
                  <StatusPill status={c.status} />
                </div>
                <dl className="grid grid-cols-3 gap-2">
                  <div>
                    <dt className="text-xs text-muted-foreground">
                      {quotaHead}
                    </dt>
                    <dd className="mt-0.5 text-[15px] font-semibold">
                      {quota(c)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">单日上限</dt>
                    <dd className="mt-0.5 text-[15px] font-semibold">
                      {c.daily_limit} 次/日
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">有效期</dt>
                    <dd className="mt-0.5 text-[15px] font-semibold">
                      {c.validity_months} 个月
                    </dd>
                  </div>
                </dl>
                <p className="text-[13px] text-muted-foreground">
                  在用会员：{memberCountText(c)}
                </p>
                <div className="flex gap-2 border-t border-dashed pt-2.5">
                  <Button
                    variant="outline"
                    className="h-11 flex-1 rounded-xl text-foreground"
                    onClick={() => onEdit(c)}
                  >
                    编辑
                  </Button>
                  <Button
                    variant="outline"
                    className={'h-11 flex-1 rounded-xl ' + (isOff(c) ? enableClass : '')}
                    onClick={() => onToggleStatus(c)}
                  >
                    {isOff(c) ? '启用' : '停用'}
                  </Button>
                </div>
              </article>
            ))}
            {rows.length === 0 && (
              <p
                className={
                  cardClass + ' py-8 text-center text-sm text-muted-foreground'
                }
              >
                暂无{modeLabel}类型
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
